import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  MessageSquare,
  Filter,
  ShieldCheck,
  Check,
  Instagram,
  Facebook,
  PlayCircle,
  Star,
  Shield,
  HelpCircle,
  Award
} from "lucide-react";

const features = [
  { icon: MessageSquare, title: "Auto-DM Replies", desc: "Comments containing your keyword instantly trigger personalized DMs with your link or PDF.", bg: "bg-peach" },
  { icon: Filter, title: "Keyword Triggers", desc: "Set multiple keywords per rule. Case-insensitive matching, with idempotency to prevent double DMs.", bg: "bg-mint" },
  { icon: Zap, title: "Lightning Fast", desc: "Webhook-driven architecture sends DMs within seconds of the comment landing.", bg: "bg-butter" },
  { icon: ShieldCheck, title: "Meta-Compliant", desc: "Built on official Instagram Messaging API. No scraping. Zero risk of account bans.", bg: "bg-sky" },
];

const steps = [
  { n: "01", title: "Connect your Instagram", desc: "Link your IG Business or Creator account in 30 seconds." },
  { n: "02", title: "Create a rule", desc: 'Pick keywords like "LINK" or "GUIDE" and write the DM you want sent.' },
  { n: "03", title: "Watch it work", desc: "Comments arrive → keyword detected → DM auto-delivered. Track every send." },
];

export default function Landing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      
      {/* 1. HERO SECTION */}
      <section className="relative grid-noise border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="chip bg-butter font-bold uppercase text-xs tracking-widest border-2 border-foreground shadow-brutal-sm">
              <Star className="w-3 h-3 mr-1 fill-current" strokeWidth={3} /> Built for creators
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight uppercase italic">
              Turn comments into <span className="bg-primary text-white px-2 inline-block border-2 border-foreground shadow-brutal -rotate-1">DMs</span>{" "}
              that <span className="bg-mint px-2 inline-block border-2 border-foreground shadow-brutal rotate-1 text-foreground">convert</span>.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl font-bold">
              Someone comments <strong className="bg-accent px-1.5 border-2 border-foreground rounded">"LINK"</strong> on your reel — <span className="text-foreground">DmFlio</span> auto-sends them your guide, lead magnet, or sales page. No manual work.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-primary text-white border-2 border-foreground rounded-md px-6 py-3.5 font-bold shadow-brutal hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all uppercase"
              >
                Start free — 40 DMs/mo <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 bg-white border-2 border-foreground rounded-md px-6 py-3.5 font-bold shadow-brutal-sm hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all uppercase"
              >
                <PlayCircle className="w-5 h-5" strokeWidth={2.5} /> See how it works
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-accent border-2 border-foreground rounded-md shadow-brutal -rotate-6"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-sky border-2 border-foreground rounded-md shadow-brutal rotate-6"></div>
              <div className="relative border-4 border-foreground overflow-hidden rotate-1 bg-white p-1 shadow-brutal">
                <img
                  src="https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&q=80&w=800"
                  alt="Creator filming"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <div className="absolute -left-6 bottom-8 border-4 border-foreground bg-white p-4 max-w-[260px] -rotate-3 hidden md:block shadow-brutal">
                <div className="flex items-center gap-2 mb-2">
                  <Instagram className="w-4 h-4 text-primary" strokeWidth={2.5} />
                  <div className="text-xs font-black uppercase italic">@yourbrand</div>
                </div>
                <div className="text-sm font-bold leading-snug">
                  "Hey! Here's the free guide 👇 <br />
                  <span className="underline text-primary">dmflio.com/guide</span>"
                </div>
                <div className="mt-2 chip bg-mint text-[10px] font-black uppercase border-2 border-foreground">DM sent in 1.2s</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MARQUEE */}
      <div className="border-y-2 border-foreground bg-accent py-4 overflow-hidden">
        <div className="flex whitespace-nowrap font-black italic text-2xl uppercase tracking-tighter animate-marquee">
          {[..."LINK · GUIDE · INFO · SALE · PDF · DROP · WAITLIST · MENU · BOOK · FREEBIE · "
            .repeat(8)].map((c, i) => <span key={i} className="mx-4">{c}</span>)}
        </div>
      </div>

      {/* 3. FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-12">
          <span className="chip bg-mint border-2 border-foreground font-bold uppercase text-xs">What you get</span>
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mt-4 leading-tight tracking-tighter">
            One job. Done <span className="text-primary underline decoration-4">obsessively</span> well.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className={`border-4 border-foreground p-6 ${f.bg} shadow-brutal hover:-translate-y-1 transition-all`}>
              <div className="w-11 h-11 bg-white border-2 border-foreground rounded-md flex items-center justify-center mb-4 shadow-brutal-sm">
                <f.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <h3 className="font-black uppercase text-xl mb-2 italic tracking-tight">{f.title}</h3>
              <p className="text-sm font-bold leading-relaxed opacity-80">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. USE CASES */}
      <section className="py-20 px-6 border-y-4 border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black uppercase italic mb-12 tracking-tighter text-center">Engineered for every niche.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 border-4 border-white/20 hover:border-primary transition-colors">
                <h3 className="text-xl font-black uppercase italic mb-2">Education</h3>
                <p className="font-bold text-gray-400">Deliver course links and PDFs instantly to students who comment on your lessons.</p>
            </div>
            <div className="p-8 border-4 border-white/20 hover:border-primary transition-colors">
                <h3 className="text-xl font-black uppercase italic mb-2">Commerce</h3>
                <p className="font-bold text-gray-400">Automatically send product checkout links when customers ask for "Price" or "Info".</p>
            </div>
            <div className="p-8 border-4 border-white/20 hover:border-primary transition-colors">
                <h3 className="text-xl font-black uppercase italic mb-2">Services</h3>
                <p className="font-bold text-gray-400">Share booking calendars and portfolio links the second a lead shows interest.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. HOW IT WORKS */}
      <section id="how" className="bg-white border-b-4 border-foreground py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <span className="chip bg-peach border-2 border-foreground font-bold uppercase text-xs">The Process</span>
            <h2 className="text-4xl md:text-5xl font-black uppercase italic mt-4 leading-tight tracking-tighter">
              From comment to DM in 3 steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="border-4 border-foreground bg-white p-6 shadow-brutal transition-transform hover:-rotate-1">
                <div className="font-black italic text-5xl mb-4 text-primary">{s.n}</div>
                <h3 className="font-black uppercase text-xl mb-2 tracking-tight">{s.title}</h3>
                <p className="text-sm font-bold text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. SECURITY SECTION */}
      <section className="py-20 px-6 bg-sky/10 border-b-4 border-foreground">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
                <Shield className="w-12 h-12 text-primary mb-6" />
                <h2 className="text-4xl font-black uppercase italic mb-6 tracking-tighter leading-none">Safe. Secure. <br />Official.</h2>
                <p className="font-bold text-lg text-muted-foreground leading-relaxed">
                    DmFlio is built exclusively on the Official Meta Messaging API. We do not use automation bots or browser scraping, ensuring your account remains 100% compliant with Instagram’s Terms of Service.
                </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className="p-6 border-4 border-foreground bg-white font-black uppercase text-xs shadow-brutal-sm flex flex-col items-center gap-2">
                    <Award className="text-primary w-6 h-6" /> Meta Partner API
                </div>
                <div className="p-6 border-4 border-foreground bg-white font-black uppercase text-xs shadow-brutal-sm flex flex-col items-center gap-2">
                    <ShieldCheck className="text-primary w-6 h-6" /> Anti-Ban Tech
                </div>
            </div>
        </div>
      </section>

      {/* 7. PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="chip bg-butter border-2 border-foreground font-bold uppercase text-xs tracking-widest">Investment</span>
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mt-4 leading-tight tracking-tighter">Simple pricing.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="border-4 border-foreground bg-white p-8 shadow-brutal relative transition-transform hover:scale-[1.02]">
            <h3 className="font-black uppercase text-2xl italic tracking-tight">Free Tier</h3>
            <div className="my-4">
              <span className="font-black text-6xl italic italic tracking-tighter">₹0</span>
              <span className="text-muted-foreground font-black uppercase text-xs ml-2">/forever</span>
            </div>
            <p className="text-sm font-bold text-muted-foreground mb-6 italic border-b-2 border-foreground pb-2">For creators just starting out.</p>
            <ul className="space-y-3 text-xs font-black uppercase tracking-tight mb-8">
              {["40 DMs / month", "Unlimited rules", "Comment-to-DM automation", "Trigger history & analytics"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" strokeWidth={4} />{t}</li>
              ))}
            </ul>
            <Link to="/register" className="block text-center w-full px-4 py-4 border-4 border-foreground font-black uppercase text-lg shadow-brutal-sm hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
              Get started free
            </Link>
          </div>

          <div className="border-4 border-foreground bg-primary p-8 shadow-brutal relative transition-transform hover:scale-[1.02]">
            <div className="absolute -top-3 right-6 bg-accent border-2 border-foreground px-3 py-0.5 font-black uppercase text-[10px] shadow-brutal-sm">Most popular</div>
            <h3 className="font-black uppercase text-2xl italic tracking-tight text-white">Pro Tier</h3>
            <div className="my-4 text-white">
              <span className="font-black text-6xl italic tracking-tighter">₹199</span>
              <span className="font-black uppercase text-xs ml-2 opacity-80">/month</span>
            </div>
            <p className="text-sm font-bold text-white/90 mb-6 italic border-b-2 border-white/30 pb-2">For creators serious about converting.</p>
            <ul className="space-y-3 text-xs font-black uppercase tracking-tight mb-8 text-white">
              {["Unlimited DMs", "Priority delivery", "Full analytics", "Priority support"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-white" strokeWidth={4} />{t}</li>
              ))}
            </ul>
            <Link to="/register" className="block text-center w-full px-4 py-4 bg-foreground text-white border-4 border-foreground font-black uppercase text-lg shadow-[4px_4px_0px_0px_white] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
              Upgrade to Pro
            </Link>
          </div>
        </div>
      </section>

      {/* 8. FAQ SECTION */}
      <section className="py-24 px-4 bg-white border-y-4 border-foreground">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-black uppercase italic tracking-tighter text-center mb-16">Intelligence Base</h2>
          <div className="space-y-6">
            <div className="border-4 border-foreground p-6 bg-[#fdfcf7] shadow-brutal-sm">
                <h3 className="font-black uppercase text-lg mb-3 italic flex items-center gap-3"><HelpCircle className="text-primary" /> Is my account safe?</h3>
                <p className="font-bold text-muted-foreground text-sm">Yes. We use the official Meta Graph API. We never require your password and operate strictly within Instagram's developer guidelines.</p>
            </div>
            <div className="border-4 border-foreground p-6 bg-[#fdfcf7] shadow-brutal-sm">
                <h3 className="font-black uppercase text-lg mb-3 italic flex items-center gap-3"><HelpCircle className="text-primary" /> How fast are the replies?</h3>
                <p className="font-bold text-muted-foreground text-sm">Replies are triggered via real-time webhooks, meaning messages are usually delivered in under 2 seconds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 10. SOCIAL MEDIA SECTION */}
      <section className="py-24 px-6 bg-mint border-y-4 border-foreground relative overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
          <div className="space-y-4 text-center md:text-left">
            <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter leading-none">
              Join the <br /><span className="text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]">Community.</span>
            </h2>
            <p className="font-bold text-lg max-w-md">
              Follow us for automation hacks, creator growth strategies, and real-time platform updates.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="https://instagram.com/dmflio" 
              className="bg-white border-4 border-foreground p-6 shadow-brutal flex items-center gap-4 hover:bg-primary hover:text-white transition-all group"
            >
              <Instagram className="w-8 h-8" />
              <div className="text-left">
                <div className="font-black uppercase text-xs">Instagram</div>
                <div className="font-bold text-sm opacity-70 group-hover:text-white">@dmflio</div>
              </div>
            </a>

            <a 
              href="https://www.facebook.com/people/DmFlio/61560681520544/" 
              className="bg-white border-4 border-foreground p-6 shadow-brutal flex items-center gap-4 hover:bg-primary hover:text-white transition-all group"
            >
              <Facebook className="w-8 h-8" />
              <div className="text-left">
                <div className="font-black uppercase text-xs">Facebook</div>
                <div className="font-bold text-sm opacity-70 group-hover:text-white">DmFlio Official</div>
              </div>
            </a>
          </div>
        </div>
        
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-black text-[20vw] uppercase italic text-black/5 pointer-events-none whitespace-nowrap">
          SOCIAL • SOCIAL • SOCIAL
        </div>
      </section>

      {/* 11. FINAL CTA SECTION */}
      <section className="bg-foreground text-white py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-6xl font-black uppercase italic mb-6 leading-tight tracking-tighter">
            Stop typing. <br />Start Growing.
          </h2>
          <p className="text-xl text-white/70 mb-10 font-bold italic">
            Set it once. DmFlio handles the rest.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-3 bg-primary text-white border-4 border-white px-10 py-5 font-black uppercase text-2xl shadow-[6px_6px_0px_0px_white] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
          >
            Go Live Now <ArrowRight className="w-8 h-8" strokeWidth={4} />
          </Link>
        </div>
      </section>

    </div>
  );
}
